import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Send, CheckCircle2, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { qd, money, STATUS_COLORS } from '../api';

const CARDS = [
  { key: 'total', label: 'Total quotes', icon: FileText, color: 'text-slate-600' },
  { key: 'sent', label: 'Sent', icon: Send, color: 'text-blue-600' },
  { key: 'accepted', label: 'Accepted', icon: CheckCircle2, color: 'text-emerald-600' },
  { key: 'expired', label: 'Expired', icon: Clock, color: 'text-red-500' },
];

const count = (qs, status) => qs.filter((q) => q.status === status).length;
const sum = (qs, status) => qs.filter((q) => q.status === status).reduce((a, q) => a + Number(q.totals?.net || 0), 0);

const monthlyData = (quotes) => {
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleString('en-US', { month: 'short', year: '2-digit' }), sent: 0, accepted: 0 });
  }
  const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
  quotes.forEach((q) => {
    if (q.status !== 'sent' && q.status !== 'accepted') return;
    const key = (q.sent_at || q.created_at || '').slice(0, 7);
    if (byKey[key]) byKey[key][q.status] += 1;
  });
  return months;
};

export default function QdDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [bannerStats, setBannerStats] = useState(null);

  useEffect(() => {
    qd.get('/quotes').then((r) => setQuotes(r.data));
    qd.get('/entities').then((r) => setEntities(r.data));
    qd.get('/track/banner-stats', { baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` })
      .then((r) => setBannerStats(r.data))
      .catch(() => setBannerStats(null));
  }, []);

  const stats = {
    total: quotes.length,
    sent: count(quotes, 'sent'),
    accepted: count(quotes, 'accepted'),
    expired: count(quotes, 'expired'),
  };
  const recent = [...quotes].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5);

  return (
    <div className="p-6 md:p-8" data-testid="qd-dashboard">
      <h1 className="text-xl font-bold text-[#14233F] mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-white border border-slate-200 rounded p-5" data-testid={`qd-stat-${c.key}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</p>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </div>
            <p className="text-3xl font-bold text-[#14233F] mono">{stats[c.key]}</p>
          </div>
        ))}
      </div>

      <h2 className="text-sm font-bold text-[#14233F] uppercase tracking-wider mb-3">Sent vs accepted by month</h2>
      <div className="bg-white border border-slate-200 rounded p-4 mb-8" data-testid="qd-monthly-chart">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthlyData(quotes)} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="sent" name="Sent" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={28} />
            <Bar dataKey="accepted" name="Accepted" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <h2 className="text-sm font-bold text-[#14233F] uppercase tracking-wider mb-3">By entity</h2>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto mb-8">
        <table className="w-full text-sm min-w-[700px]" data-testid="qd-entity-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3 font-semibold">Entity</th>
              <th className="text-right px-4 py-3 font-semibold">Draft</th>
              <th className="text-right px-4 py-3 font-semibold">Sent</th>
              <th className="text-right px-4 py-3 font-semibold">Accepted</th>
              <th className="text-right px-4 py-3 font-semibold">Expired</th>
              <th className="text-right px-4 py-3 font-semibold">Sent value</th>
              <th className="text-right px-4 py-3 font-semibold">Accepted value</th>
            </tr>
          </thead>
          <tbody>
            {entities.map((e) => {
              const eq = quotes.filter((q) => q.entity_id === e.id);
              return (
                <tr key={e.id} className="border-b border-slate-100" data-testid={`qd-entity-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{e.name}</p>
                    <p className="text-[11px] text-slate-400 mono uppercase">{e.prefix} · {e.country}</p>
                  </td>
                  <td className="px-4 py-3 mono text-right text-slate-500">{count(eq, 'draft')}</td>
                  <td className="px-4 py-3 mono text-right text-blue-700">{count(eq, 'sent')}</td>
                  <td className="px-4 py-3 mono text-right text-emerald-700">{count(eq, 'accepted')}</td>
                  <td className="px-4 py-3 mono text-right text-red-600">{count(eq, 'expired')}</td>
                  <td className="px-4 py-3 mono text-right">{money(sum(eq, 'sent'))}</td>
                  <td className="px-4 py-3 mono text-right font-semibold">{money(sum(eq, 'accepted'))}</td>
                </tr>
              );
            })}
            {entities.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400 text-sm">Loading…</td></tr>}
          </tbody>
        </table>
      </div>

      <h2 className="text-sm font-bold text-[#14233F] uppercase tracking-wider mb-3">Recent quotes</h2>
      <div className="bg-white border border-slate-200 rounded divide-y divide-slate-100" data-testid="qd-recent-quotes">
        {recent.map((q) => (
          <Link key={q.id} to={`/quote/devis/${q.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
            <div>
              <span className="mono font-semibold text-[#0F7D70] text-sm">{q.number}</span>
              <span className="text-xs text-slate-500 ml-3">{q.client_snapshot?.name}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="mono text-xs text-slate-500">{money(q.totals?.net)}</span>
              <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${STATUS_COLORS[q.status]}`}>{q.status}</span>
            </div>
          </Link>
        ))}
        {recent.length === 0 && <p className="px-4 py-8 text-center text-slate-400 text-sm">No quotes yet.</p>}
      </div>

      {bannerStats && (
        <>
          <h2 className="text-sm font-bold text-[#14233F] uppercase tracking-wider mb-3 mt-8">TEKTELIC banner clicks (0gbaltics.com/tektelic)</h2>
          <div className="bg-white border border-slate-200 rounded overflow-x-auto" data-testid="qd-banner-stats">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold">Destination</th>
                  <th className="text-right px-4 py-3 font-semibold">Total clicks</th>
                  <th className="text-right px-4 py-3 font-semibold">Last 30 days</th>
                  <th className="text-left px-4 py-3 font-semibold">By language</th>
                </tr>
              </thead>
              <tbody>
                {bannerStats.map((s) => (
                  <tr key={s.target} className="border-b border-slate-100" data-testid={`qd-banner-row-${s.target}`}>
                    <td className="px-4 py-3 mono font-semibold text-[#0F7D70]">{s.target}</td>
                    <td className="px-4 py-3 mono text-right font-semibold">{s.total}</td>
                    <td className="px-4 py-3 mono text-right">{s.last30}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {Object.entries(s.by_lang).map(([l, n]) => `${l.toUpperCase()}: ${n}`).join(' · ')}
                    </td>
                  </tr>
                ))}
                {bannerStats.length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-400 text-sm">No banner clicks recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
